<template>
  <div class="contact-info-wrapper">
    <ul
      id="contact-info-accordion"
      class="accordion accordion-customize"
      data-accordion
      data-deep-link="true"
      data-allow-all-closed="true"
      data-multi-expand="true"
      data-update-history="true"
      data-deep-link-smudge="500"
    >
      <li
        class="accordion-item is-active"
        data-accordion-item
      >
        <a class="accordion-title">
          <span>{{ $t('contact.contactInfo') }}</span>
          <span
            v-if="!isCreatePage"
            class="accordion-title__histories name-hyper-link float-right"
            @click.stop.prevent="$refs.updateHistoriesModal.open"
          >
            {{ $t('tenancy.changeHistory') }}
          </span>
        </a>
        <div
          class="accordion-content"
          data-tab-content
        >
          <div class="row">
            <div
              v-for="(fullnameObj, index) of getFullnameSorts"
              :key="`fullname_${index}`"
              class="input-set small-3 medium-3 large-3 column"
              :class="fullnameObj.isFirst ? 'first-name' : 'last-name'"
            >
              <label
                class="emphasis"
                :class="{ 'required-flag': allowEdit && fullnameObj.label === 'firstName' }"
              >
                {{ $t(`contact.basic.${fullnameObj.label === 'firstName' ? 'firstGroupName' : fullnameObj.label}`) }}
              </label>
              <input
                v-if="allowEdit"
                v-hkldinput
                v-fusioninputcheck="fullnameObj.label === 'firstName' ? {
                  type: 'string',
                  tip: $t('hint.inputTip.string'),
                  classes: 'tooltip-class'
                } : { required: false }"
                type="text"
                :value="currentContact[fullnameObj.value]"
                :required="fullnameObj.label === 'firstName'"
                @change="event => updatePathField(fullnameObj.value, event.target.value)"
              >
              <div v-if="!(allowEdit)">
                {{ currentContact[fullnameObj.value] }}
              </div>
            </div>
            <div class="prefix input-set small-2 medium-2 large-2 column">
              <label class="emphasis">{{ $t('contact.basic.prefix') }}</label>
              <HKLSelect
                v-if="allowEdit"
                :value="currentContact.prefix || undefined"
                :options="prefixHklOptions"
                :showSearch="true"
                placeholder=""
                @change="val => updatePathField('prefix', val)"
              />
              <div v-if="!(allowEdit)">
                {{ customPrefixLabel(currentContact.prefix) }}
              </div>
            </div>
          </div>
          <div
            v-if="isEngineer"
            class="remarks-block"
          >
            <label class="emphasis">{{ $t('contact.basic.remarks') }}</label>
            <TextField
              v-hkldinput
              :value="currentContact.remarks || (allowEdit ? '' : emptyValue)"
              :readonly="!allowEdit"
              @updateValue="val => updatePathField('remarks', val)"
            />
          </div>

          <div
            class="table-section"
            :class="{'error-highlight-background': inputRequireHighlight || highlightPhone}"
          >
            <table class="contacts-table">
              <thead>
                <tr>
                  <th class="selct-column">
                    {{ $t('contact.phone.primaryPhone') }}
                  </th>
                  <th class="number-column">
                    {{ $t('contact.phone.phoneNumber') }}
                  </th>
                  <th class="type-column">
                    {{ $t('contact.phone.phoneType') }}
                  </th>
                  <th class="remove-column" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in currentContact.phoneNumbers"
                  v-if="currentContact.phoneNumbers.length !== 0"
                  :key="index"
                >
                  <td class="selct-column">
                    <input
                      type="radio"
                      name="phoneNumbers"
                      :disabled="!allowEdit"
                      :checked="item.priority ? 'checked': ''"
                      @change="event =>updateContactInfo('phoneNumbers', index, ['priority'], !item.priority)"
                    >
                  </td>
                  <td class="number-column">
                    <input
                      v-if="allowEdit"
                      v-hkldinput
                      v-fusioninputcheck="{
                        type: 'string',
                        pattern: regex.phone,
                        classes: 'tooltip-class',
                        tip: tips.phone,
                        required: true
                      }"
                      type="text"
                      :value="item.value"
                      @change="event => updatePathArrayField('phoneNumbers', index, ['value'], event.target.value)"
                    >
                    <div v-if="!allowEdit">
                      {{ item.value || '--' }}
                    </div>
                  </td>
                  <td class="type-column">
                    <HKLSelect
                      v-if="allowEdit"
                      :value="deep(item, 'typeRemark') || undefined"
                      :options="phoneTypeHklOptions"
                      :showSearch="true"
                      placeholder=""
                      @change="val => updatePathArrayField('phoneNumbers', index, ['typeRemark'], val)"
                    />
                    <div v-if="!allowEdit">
                      {{ customContactType(item.typeRemark) }}
                    </div>
                  </td>
                  <td class="icon-remove">
                    <i
                      v-if="allowEdit"
                      class="fa fa-times-thin"
                      @click="openDeleteConfirm('phoneNumbers', index)"
                    />
                  </td>
                </tr>
                <tr v-if="currentContact.phoneNumbers.length === 0 && !allowEdit">
                  <td class="selct-column">
                    {{ emptyValue }}
                  </td>
                  <td class="number-column">
                    {{ emptyValue }}
                  </td>
                  <td class="type-column">
                    {{ emptyValue }}
                  </td>
                  <td class="icon-remove" />
                </tr>
              </tbody>
            </table>
            <button
              v-if="allowEdit"
              class="btn btn--round"
              @click="addItem('phoneNumbers')"
            >
              {{ $t('contact.phone.addPhone') }} +
            </button>
          </div>

          <div
            class="table-section"
            :class="{'error-highlight-background': inputRequireHighlight || highlightEmail}"
          >
            <table class="contacts-table">
              <thead>
                <tr>
                  <th class="selct-column">
                    {{ $t('contact.email.primaryEmail') }}
                  </th>
                  <th class="number-column">
                    {{ $t('contact.email.emailAddr') }}
                  </th>
                  <th class="type-column">
                    {{ $t('contact.email.emailType') }}
                  </th>
                  <th class="remove-column" />
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, index) in currentContact.emails"
                  v-if="currentContact.emails.length !== 0"
                  :key="index"
                >
                  <td class="selct-column">
                    <input
                      type="radio"
                      name="emails"
                      :disabled="!allowEdit"
                      :checked="item.priority"
                      @change="event =>updateContactInfo('emails', index, ['priority'], !item.priority)"
                    >
                  </td>
                  <td class="number-column">
                    <input
                      v-if="allowEdit"
                      v-hkldinput
                      v-fusioninputcheck="{
                        type: 'string',
                        pattern: regex.email,
                        classes: 'tooltip-class',
                        tip: tips.email,
                        required: true
                      }"
                      type="text"
                      :value="deep(item, 'value')"
                      @change="event => updatePathArrayField('emails', index, ['value'], event.target.value)"
                    >
                    <div v-if="!allowEdit">
                      {{ item.value || '--' }}
                    </div>
                  </td>
                  <td class="type-column">
                    <HKLSelect
                      v-if="allowEdit"
                      :value="deep(item, 'typeRemark') || undefined"
                      :options="emailTypeHklOptions"
                      :showSearch="true"
                      placeholder=""
                      @change="val => updatePathArrayField('emails', index, ['typeRemark'], val)"
                    />
                    <div v-if="!allowEdit">
                      {{ customContactType(item.typeRemark) }}
                    </div>
                  </td>
                  <td class="icon-remove">
                    <i
                      v-if="allowEdit"
                      class="fa fa-times-thin"
                      @click="openDeleteConfirm('emails', index)"
                    />
                  </td>
                </tr>
                <tr v-if="currentContact.emails.length === 0 && !allowEdit">
                  <td class="selct-column">
                    {{ emptyValue }}
                  </td>
                  <td class="number-column">
                    {{ emptyValue }}
                  </td>
                  <td class="type-column">
                    {{ emptyValue }}
                  </td>
                  <td class="icon-remove" />
                </tr>
              </tbody>
            </table>
            <button
              v-if="allowEdit"
              class="btn btn--round"
              @click="addItem('emails')"
            >
              {{ $t('contact.email.addEmail') }} +
            </button>
          </div>

          <div
            class="table-section"
            :class="{'error-highlight-background': inputRequireHighlight}"
          >
            <table class="contacts-table">
              <thead>
                <tr>
                  <th class="selct-column">
                    {{ $t('contact.fax.primaryFax') }}
                  </th>
                  <th class="number-column">
                    {{ $t('contact.fax.fax') }}
                  </th>
                  <th class="type-column">
                    {{ $t('contact.fax.faxType') }}
                  </th>
                  <th class="remove-column" />
                </tr>
              </thead>
              <tbody>
                <template v-if="currentContact.faxNumbers.length !== 0">
                  <tr
                    v-for="(item, index) in currentContact.faxNumbers"
                    :key="index"
                  >
                    <td class="selct-column">
                      <input
                        type="radio"
                        name="faxNumbers"
                        :disabled="!allowEdit"
                        :checked="item.priority"
                        @change="event =>updateContactInfo('faxNumbers', index, ['priority'], !item.priority)"
                      >
                    </td>
                    <td class="number-column">
                      <input
                        v-if="allowEdit"
                        v-hkldinput
                        v-fusioninputcheck="{
                          type: 'string',
                          pattern: regex.phone,
                          classes: 'tooltip-class',
                          tip: tips.phone,
                          required: true
                        }"
                        type="text"
                        :value="deep(item, 'value')"
                        @change="event => updatePathArrayField('faxNumbers', index, ['value'], event.target.value)"
                      >
                      <div v-if="!allowEdit">
                        {{ item.value || '--' }}
                      </div>
                    </td>
                    <td class="type-column">
                      <HKLSelect
                        v-if="allowEdit"
                        :value="deep(item, 'typeRemark') || undefined"
                        :options="faxTypeHklOptions"
                        :showSearch="true"
                        placeholder=""
                        @change="val => updatePathArrayField('faxNumbers', index, ['typeRemark'], val)"
                      />
                      <div v-if="!allowEdit">
                        {{ customContactType(item.typeRemark) }}
                      </div>
                    </td>
                    <td class="icon-remove">
                      <i
                        v-if="allowEdit"
                        class="fa fa-times-thin"
                        @click="openDeleteConfirm('faxNumbers', index)"
                      />
                    </td>
                  </tr>
                </template>
                <tr v-if="currentContact.faxNumbers.length === 0 && !allowEdit">
                  <td class="selct-column">
                    {{ emptyValue }}
                  </td>
                  <td class="number-column">
                    {{ emptyValue }}
                  </td>
                  <td class="type-column">
                    {{ emptyValue }}
                  </td>
                  <td class="icon-remove" />
                </tr>
              </tbody>
            </table>
            <button
              v-if="allowEdit"
              class="btn btn--round"
              @click="addItem('faxNumbers')"
            >
              {{ $t('contact.fax.addFax') }} +
            </button>
          </div>

          <div
            v-if=" allowEdit||currentContact.addresses.length> 0"
            class="row table-section"
            :class="{'error-highlight-background': inputRequireHighlight}"
          >
            <ul class="address-list">
              <li
                v-for="(item, index) in currentContact.addresses"
                :key="index"
                class="row"
                :class="{'error-highlight-background': highlightAddressIndexes.includes(index)}"
              >
                <div class="small-2 medium-2 large-2 column select-column">
                  <div class="input-set">
                    <label class="emphasis">{{ $t('contact.address.primaryAddr') }}</label>
                    <input
                      type="radio"
                      name="addresses"
                      :checked="item.priority"
                      :disabled="!allowEdit"
                      @change="event =>updateContactInfo('addresses', index, ['priority'], !item.priority)"
                    >
                  </div>
                </div>
                <div class="small-10 medium-10 large-10 column">
                  <label class="emphasis">{{ $t('company.contact.addressLine') }}</label>
                  <div class="input-set">
                    <div class="row">
                      <input
                        v-if="allowEdit"
                        v-hkldinput
                        type="text"
                        :placeholder="$t('company.contact.addressLine1')"
                        :value="deep(item, 'value.addressLine1')"
                        @change="event => updatePathArrayField('addresses', index, ['value', 'addressLine1'], event.target.value)"
                      >
                      <div class="icon-remove address-remove">
                        <i
                          v-if="allowEdit"
                          class="fa fa-times-thin"
                          @click="openDeleteConfirm('addresses', index)"
                        />
                      </div>
                    </div>
                    <div v-if="!(allowEdit)">
                      {{ deep(item, 'value.addressLine1') || '--' }}
                    </div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit"
                      v-hkldinput
                      type="text"
                      :placeholder="$t('company.contact.addressLine2')"
                      :value="deep(item, 'value.addressLine2')"
                      @change="event => updatePathArrayField('addresses', index, ['value', 'addressLine2'], event.target.value)"
                    >
                    <div v-if="!(allowEdit)">
                      {{ deep(item, 'value.addressLine2') || '--' }}
                    </div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit"
                      v-hkldinput
                      type="text"
                      :placeholder="$t('company.contact.addressLine3')"
                      :value="deep(item, 'value.addressLine3')"
                      @change="event => updatePathArrayField('addresses', index, ['value', 'addressLine3'], event.target.value)"
                    >
                    <div v-if="!(allowEdit)">
                      {{ deep(item, 'value.addressLine3') || '--' }}
                    </div>
                  </div>
                  <div class="input-set">
                    <input
                      v-if="allowEdit"
                      v-hkldinput
                      type="text"
                      :placeholder="$t('company.contact.addressLine4')"
                      :value="deep(item, 'value.addressLine4')"
                      @change="event => updatePathArrayField('addresses', index, ['value', 'addressLine4'], event.target.value)"
                    >
                    <div v-if="!(allowEdit)">
                      {{ deep(item, 'value.addressLine4') || '--' }}
                    </div>
                  </div>
                  <div class="row">
                    <div class="input-set small-4 medium-4 large-4 column">
                      <label class="emphasis">{{ $t('contact.address.addrType') }}</label>
                      <HKLSelect
                        v-if="allowEdit"
                        :value="deep(item, 'typeRemark') || undefined"
                        :options="addressTypeHklOptions"
                        :showSearch="true"
                        placeholder=""
                        @change="val => updatePathArrayField('addresses', index, ['typeRemark'], val)"
                      />
                      <div v-if="!(allowEdit)">
                        {{ customContactType(item.typeRemark) }}
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
            <button
              v-if="allowEdit"
              class="btn btn--round"
              @click="addItem('addresses')"
            >
              {{ $t('contact.address.addAddr') }} +
            </button>
          </div>
        </div>
      </li>
    </ul>
    <Modal
      ref="updateHistoriesModal"
      modalId="contact-histories-modal"
      modalType="large-modal"
    >
      <div
        slot="modal-header"
        class="modal-header"
      >
        {{ $t('tenancy.changeHistory') }}
      </div>
      <div
        slot="content"
        class="contact-histories-modal__content"
      >
        <UpdateHistories
          :sourceId="contactId"
          sourceTable="ccd_contacts"
        />
      </div>
    </Modal>
    <Modal
      ref="alertModal"
      modalId="action-not-allowed-alert"
    >
      <div
        slot="content"
        class="modal-pure-text"
      >
        <p class="font-bold">
          {{ alertMessage }}
        </p>
      </div>
      <div slot="btns">
        <button
          class="btn btn--orange"
          @click="closeAlertModal"
        >
          {{ $t('action.ok') }}
        </button>
      </div>
    </Modal>
    <ActionConfirmModal
      ref="deleteConfirmModal"
      :modalId="delConfModalId"
      :confirmTitle="confirmMessage"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import Modal from '@/controls/Modal'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import TextField from '@/controls/TextField'
import UpdateHistories from '@/controls/UpdateHistories'
import ActionConfirmModal from '@/controls/ActionConfirmModal'
import formFullnameMixin from '@/pages/formFullnameMixin'
import EventBus from '@/utils/eventBus'
import mutationTypes from '@/store/mutation-types'
import * as regex from '@/utils/regex'
import { utils as formUtils } from '@/plugin/InputChange'
import { ccdRouteNames } from '@/utils/consts'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { checkIsLinkedTrPhone, getLinkedTrEmails } from '@/pages/ccd/tenancy.utils'
import { CCD_EMAIL_LINK_TENANCY_TYPES, CCD_PHONE_LINK_TENANCY_TYPES, TENANCY_ALL_CONTACT_TYPES_MAP } from '../tenancy/consts'

export default {
  name: 'ContactInformation',
  components: {
    HKLSelect, TextField, Modal, UpdateHistories, ActionConfirmModal,
  },
  mixins: [formFullnameMixin],
  props: {
    contactId: [String, Number],
    allowEdit: Boolean,
    inputRequireHighlight: Boolean,
    highlightEmail: Boolean,
    highlightPhone: {
      type: Boolean,
      default: false,
    },
    highlightAddressIndexes: {
      type: Array,
      default: () => ([]),
    },
    originalContact: Object,
  },
  data () {
    return {
      deep,
      regex,
      emptyValue: this.$t('emptyValue'),
      alertMessage: '',
      delConfModalId: `contactInfo-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`,
      confirmMessage: this.$t('deleteConfirmMsg'),
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentContact']),
    ...mapGetters('user', ['isEngineer']),
    tips () {
      return {
        email: this.$t('hint.inputTip.email'),
        phone: this.$t('hint.inputTip.phone'),
        string: this.$t('hint.inputTip.string'),
      }
    },
    isCreatePage () {
      const routeName = this.$router.currentRoute.name
      return [ccdRouteNames.ccdContactCreate, ccdRouteNames.tenantCcdContactCreate].includes(routeName)
    },
    prefixHklOptions () {
      return this.mapStringOptions(['Mr.', 'Ms.', 'Mrs.'], this.customPrefixLabel)
    },
    phoneTypeHklOptions () {
      return this.mapStringOptions(['Business', 'Personal', 'Mobile'], this.customContactType)
    },
    emailTypeHklOptions () {
      return this.mapStringOptions(['Business', 'Personal'], this.customContactType)
    },
    faxTypeHklOptions () {
      return this.mapStringOptions(['Business', 'Personal'], this.customContactType)
    },
    addressTypeHklOptions () {
      return this.mapStringOptions(['Work address', 'Personal address'], this.customContactType)
    },
  },
  mounted () {
    if (!this.contactInfoAccordion) {
      this.contactInfoAccordion = new Foundation.Accordion(($('#contact-info-accordion')))
    }
  },
  destroyed () {
    if (this.contactInfoAccordion) {
      this.contactInfoAccordion.destroy()
    }
  },
  methods: {
    ...mapMutations('ccd', {
      updateContactPathField: mutationTypes.CONTACT_UPDATE_PATH_FIELD,
      updateContactPathArrayField: mutationTypes.CONTACT_UPDATE_PATH_ARRAY_FIELD,
      pushItemToArray: mutationTypes.CONTACT_PUSH_ITEM_TO_ARRAY,
    }),
    ...mapActions('ccd', [
      'loadContact',
    ]),
    mapStringOptions (options, labelFn) {
      return (options || []).map((value) => ({
        name: labelFn(value),
        value,
      }))
    },
    customContactType (option) {
      if (!option) return '--'
      const key = option.toLowerCase()
      return this.$t(`company.contact.type.${key}`)
    },
    customPrefixLabel (option) {
      return option ? this.$t(`prefix.${option.replace(/\W/g, '')}`) : '--'
    },
    updatePathField (path, value) {
      formUtils.setDirty()
      this.updateContactPathField({
        path: [path],
        newValue: value,
      })
    },
    updatePathArrayField (arrayName, index, path, value) {
      formUtils.setDirty()
      this.updateContactPathArrayField({
        arrayName: arrayName,
        index: index,
        path: path,
        newValue: value,
      })
      EventBus.$emit('checkInputRequireHighlight')
    },
    validate () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'all'}, '.contact-info-wrapper')
      return $('.contact-info-wrapper input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.contact-info-wrapper')
    },
    openDeleteConfirm (type, index) {
      fusionInputCheckUtil.resetAllFields()

      const vendorLinkedEmailIds = this.getVendorLinkedItemIds('emails')
      const vendorLinkedPhone = this.getVendorLinkedItemIds('phoneNumbers')
      const deletedItem = this.currentContact[type][index]
      const isLinkedToVendor = !lodash.get(this.currentContact, 'tenancies', []).length
      const isRceECircular = (this.currentContact.companies || []).some(company => company.CompanyContact.isVendorCircular)
      const ifContactRecEcircular = deletedItem && isLinkedToVendor && isRceECircular
      if (deletedItem && type === 'emails' && ifContactRecEcircular && vendorLinkedEmailIds.includes(deletedItem.id)) {
        this.confirmMessage = this.$t('hint.contact.email.remindTheDelete')
      } else if (deletedItem && type === 'phoneNumbers' && this.currentContact.id && isLinkedToVendor && vendorLinkedPhone.includes(deletedItem.id)) {
        this.confirmMessage = this.$t('hint.contact.phoneNum.allowToDelete')
      } else {
        this.confirmMessage = this.$t('deleteConfirmMsg')
      }
      this.$refs.deleteConfirmModal.open(this.deleteItem, [type, index, ifContactRecEcircular], this.validate)
    },
    addItem (type, defaultValue = '') {
      const toAddTo = this.currentContact[type]
      const priority = Array.isArray(toAddTo) && toAddTo.length === 0
      const addressItem = {
        priority,
        value: {
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          addressLine4: '',
        },
        description: '',
        typeRemark: '',
      }
      const otherItem = {
        priority,
        value: defaultValue,
        description: '',
        typeRemark: '',
      }
      const newItem = type === 'addresses' ? addressItem : otherItem
      const newValue = lodash.cloneDeep(toAddTo)
      newValue.push(newItem)
      this.pushItemToArray({
        arrayName: type,
        newValue,
      })
      this.$forceUpdate()
    },
    deleteItem (type, index, ifContactRecEcircular) {
      const deletedItem = this.currentContact[type][index]
      const newValue = lodash.cloneDeep(this.currentContact[type])
      if (deletedItem && type === 'emails') {
        if (ifContactRecEcircular && this.currentContact.emails.length <= 1) {
          this.openHintCCDVendorEmailsAlert()
          return
        }
        const emailsInUse = getLinkedTrEmails(
          this.originalContact.tenancies,
          CCD_EMAIL_LINK_TENANCY_TYPES,
          TENANCY_ALL_CONTACT_TYPES_MAP,
        )
        const isLinkedTREmail = deletedItem && emailsInUse.includes(deletedItem.value)
        if (isLinkedTREmail) {
          this.openHintCCDEmailsAlert()
          return
        }
      }
      if (deletedItem && type === 'phoneNumbers') {
        const tenancies = lodash.get(this.currentContact, 'tenancies', [])
        const isPhoneInUsed = checkIsLinkedTrPhone(tenancies, CCD_PHONE_LINK_TENANCY_TYPES)
        if (this.currentContact.phoneNumbers.length === 1 && isPhoneInUsed) {
          this.openHintCCDPhonesAlert()
          return
        }
      }
      newValue.splice(index, 1)
      const isPriorityItem = this.currentContact[type][index].priority
      if (isPriorityItem && newValue.length > 0) {
        newValue[0].priority = true
      }
      this.pushItemToArray({
        arrayName: type,
        newValue,
      })
      this.$forceUpdate()
      this.validate()
    },
    updateContactInfo (arrayName, index, path, value) {
      this.currentContact[arrayName].forEach((item, index) => {
        this.updatePathArrayField(arrayName, index, path, false)
      })
      this.updatePathArrayField(arrayName, index, path, value)
    },
    getECirluarContacts () {
      let eCircularContacts = []
      const tenancies = this.originalContact.tenancies
      if (!tenancies) {
        eCircularContacts = []
      } else {
        for (const key in tenancies) {
          const tenancy = tenancies[key]
          if (lodash.get(tenancy, 'CompanyContact.isCircular')) {
            const cEmails = lodash.get(tenancy, 'CompanyContact.cEmails')
            eCircularContacts.push(...cEmails)
          }
        }
      }
      return lodash.uniq(eCircularContacts)
    },
    getMarketingContacts () {
      let marketingContacts = []
      const tenancies = this.originalContact.tenancies
      if (!tenancies) {
        marketingContacts = []
      } else {
        for (const key in tenancies) {
          const tenancy = tenancies[key]
          if (lodash.get(tenancy, 'CompanyContact.isMarketing')) {
            const mEmails = lodash.get(tenancy, 'CompanyContact.mEmails')
            marketingContacts.push(...mEmails)
          }
        }
      }
      return lodash.uniq(marketingContacts)
    },
    getVendorLinkedItemIds (path) {
      let vendorLinkedIds = []
      const companies = this.originalContact.companies
      if (companies && companies.length) {
        vendorLinkedIds = this.originalContact[path].map(item => item.id)
      }
      return lodash.uniq(vendorLinkedIds)
    },
    openHintCCDEmailsAlert () {
      this.alertMessage = this.$t('hint.contact.hintDeleteEmailRelatedToTenancies')
      this.$refs.alertModal.open()
    },
    openHintCCDPhonesAlert () {
      this.alertMessage = this.$t('hint.contact.hintDeleteLastPhoneNumber')
      this.$refs.alertModal.open()
    },
    openHintCCDVendorEmailsAlert () {
      this.alertMessage = this.$t('hint.contact.email.notAllowToDelete')
      this.$refs.alertModal.open()
    },
    openHintCCDVendorPhonesAlert () {
      this.alertMessage = this.$t('hint.contact.phoneNum.remindTheDelete')
      this.$refs.alertModal.open()
    },
    closeAlertModal () {
      this.$refs.alertModal.close()
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/accordion';
@import '~styles/icon_remove';
.required-flag:after {
  color: #E49405;
  content: '*';
}
.label-tip {
  font-size: 12px;
  font-weight: normal;
  &.error-highlight {
    color: red;
  }
}
.row .column {
  padding-right: 20px;
  color: #0C3A58;
}

.input-set {
  margin-bottom: 3px;
  & div {
    word-wrap: break-word;
  }
}

.accordion-content {
  .first-name, .last-name {
    min-width: 30%;
  }
  .prefix, .status {
    min-width: 20%;
  }
  .remarks-block {
    & ::v-deep textarea {
      width: 100%;
    }
  }
}

.table-section {
  padding: 14px 20px;
  background: #F8F8F8;
  margin: 10px 0;

  td input[type="text"] {
    width: 100%;
  }
  tr th:first-of-type, tr td:first-of-type {
    width: 180px;
  }
  table.contacts-table{
    width: 100%
  }
  .number-column {
    word-wrap: break-word;
    max-width: 470px;
    min-width: 470px;
  }
  .type-column, .remark-column {
    width: 177px;
  }
  .remove-column {
    width: 45px;
  }
  td.selct-column {
    text-align: center;
    input[type="radio"] {
      display: inline-block;
      margin-bottom: 0;
    }
  }
  .address-list {
    width: 100%;
    margin: 0;
    padding: 0;
    li {
      padding-bottom: 10px;
    }
  }
  &.error-highlight-background {
    background-color: #f9ecea;
  }
  .error-highlight-background {
    background-color: #f9ecea;
  }
}

.select-column {
  .input-set input{
    margin-left: 70px;
  }
  .input-set textarea{
    height: 40px;
  }
}

.address-remove {
  display: inline-block;
  width: 5%;
  padding: 0;
  text-align: center;
  cursor: pointer;
  & .fa {
    line-height: 35px;
  }
}

.modal-header {
  font-weight: bold;
  margin-bottom: 15px
}

.accordion-title {
  &__histories {
    margin-right: 35px;
    font-size: 80%;
  }
}
.contact-histories-modal__content {
  max-height: 600px;
}

</style>
<style lang='scss'>
#contact-histories-modal {
  .modal.large-modal {
    overflow-y: unset;
  }
}

</style>
